// src/polaris-components/TokenRedirect.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { completeLoginSession } from '../utils/loginCompletion';
import { setTermsAccepted, setHasProfile } from '../utils/auth';
import { LoginResponse } from '../types/guardian';
import { resolvePasswordResetPath, shouldUsePasswordResetFlow } from '../utils/accessLinkPasswordFlow';
import { validateNewPassword } from '../utils/passwordValidation';
import polarisLogo from '../images/Polaris Logo.svg';
import '../styles/login-page.css';

type InvitationPreview = {
    firstName: string;
    lastName: string;
    email: string;
    passwordSetByUser: boolean;
    profileType: string;
};

export default function TokenRedirect() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refreshAuth } = useAuth();
    const invitationToken = searchParams.get('token');

    const [phase, setPhase] = useState<'loading' | 'ready-setup' | 'ready-reset' | 'error'>('loading');
    const [invitation, setInvitation] = useState<InvitationPreview | null>(null);
    const [error, setError] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const validatedRef = useRef(false);

    useEffect(() => {
        if (!invitationToken) {
            setError('No invitation token provided');
            setPhase('error');
            return;
        }

        if (validatedRef.current) {
            return;
        }
        validatedRef.current = true;

        const validateInvitation = async () => {
            try {
                const response = await fetch(
                    buildApiUrl(`api/auth/validate-invitation-token?token=${encodeURIComponent(invitationToken)}`),
                );

                const data = await response.json().catch(() => ({}));

                if (!response.ok || data.valid === false) {
                    throw new Error(data.message || data.detail || 'Invalid or expired invitation link');
                }

                setInvitation({
                    firstName: data.firstName ?? '',
                    lastName: data.lastName ?? '',
                    email: data.email ?? '',
                    passwordSetByUser: data.passwordSetByUser === true,
                    profileType: data.profileType ?? '',
                });
                setPhase(
                    shouldUsePasswordResetFlow(data.profileType, data.passwordSetByUser === true)
                        ? 'ready-reset'
                        : 'ready-setup',
                );
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to validate invitation link';
                setError(message);
                setPhase('error');
            }
        };

        void validateInvitation();
    }, [invitationToken]);

    useEffect(() => {
        if (phase !== 'error' || !error) {
            return;
        }
        const timer = window.setTimeout(() => navigate('/login'), 5000);
        return () => window.clearTimeout(timer);
    }, [phase, error, navigate]);

    async function finalizeAuthenticatedSession(loginData: LoginResponse, requirePasswordChange: boolean) {
        if (requirePasswordChange) {
            localStorage.setItem('requirePasswordChange', 'true');
            if (loginData.userId != null) {
                localStorage.setItem('loggedInUserId', String(loginData.userId));
                localStorage.setItem('userId', String(loginData.userId));
            }
            localStorage.setItem('authProvider', 'password');
            if (loginData.profileType || loginData.role) {
                localStorage.setItem('userRole', loginData.profileType || loginData.role || '');
            }
            if (loginData.hasProfile !== undefined) {
                setHasProfile(loginData.hasProfile);
            }
            setTermsAccepted(loginData.termsAndConditionsAccepted ?? false);
            await refreshAuth();

            const destination = resolvePasswordResetPath(loginData.profileType || loginData.role);
            navigate(destination, {
                state: { requirePasswordChange: true, fromPasswordReset: true },
            });
            return;
        }

        await completeLoginSession(loginData, navigate, refreshAuth);
    }

    function validatePassword(value: string): string | null {
        return validateNewPassword(value);
    }

    async function handleSetupSubmit(ev: React.FormEvent) {
        ev.preventDefault();
        setError('');

        if (!invitationToken) {
            setError('No invitation token provided');
            setPhase('error');
            return;
        }

        const passwordError = validatePassword(password);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch(buildApiUrl('api/auth/validate-invitation'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ token: invitationToken, password }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || 'Failed to set password');
            }

            const loginData = (await response.json()) as LoginResponse;
            await finalizeAuthenticatedSession(loginData, false);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to activate your account';
            setError(message);
        } finally {
            setIsSubmitting(false);
        }
    }

    async function handleResetContinue() {
        if (!invitationToken) {
            setError('No invitation token provided');
            setPhase('error');
            return;
        }

        setIsSubmitting(true);
        setError('');
        try {
            const response = await fetch(
                buildApiUrl(`api/auth/redeem-invitation?token=${encodeURIComponent(invitationToken)}`),
                { method: 'POST', credentials: 'include' },
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || 'Failed to verify reset link');
            }

            const loginData = (await response.json()) as LoginResponse;
            await finalizeAuthenticatedSession(loginData, true);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to continue password reset';
            setError(message);
        } finally {
            setIsSubmitting(false);
        }
    }

    if (phase === 'loading') {
        return (
            <main className="login-page" style={{ backgroundColor: '#000000' }} data-testid="invitation-loading">
                <div className="login-overlay" style={{ backgroundColor: '#00000066' }} />
                <section className="login-card" role="status" aria-label="Validating invitation">
                    <header className="login-head">
                        <img src={polarisLogo} alt="Polaris Athlete" className="login-logo" />
                        <p className="welcome">Validating your link…</p>
                    </header>
                    <div className="loading-spinner">
                        <div className="spinner" />
                    </div>
                </section>
            </main>
        );
    }

    if (phase === 'error') {
        return (
            <main className="login-page" style={{ backgroundColor: '#000000' }} data-testid="invitation-error">
                <div className="login-overlay" style={{ backgroundColor: '#00000066' }} />
                <section className="login-card" role="alert" aria-label="Invalid invitation">
                    <header className="login-head">
                        <img src={polarisLogo} alt="Polaris Athlete" className="login-logo" />
                        <p className="welcome">Invalid or expired link</p>
                    </header>
                    <div className="form-alert is-error">{error}</div>
                    <p className="welcome" style={{ marginTop: 16 }}>Redirecting to login…</p>
                    <button type="button" className="login-btn-primary" onClick={() => navigate('/login')}>
                        Go to login
                    </button>
                </section>
            </main>
        );
    }

    const greetingName = invitation?.firstName?.trim() || 'there';

    if (phase === 'ready-reset') {
        return (
            <main className="login-page" style={{ backgroundColor: '#000000' }} data-testid="invitation-reset">
                <div className="login-overlay" style={{ backgroundColor: '#00000066' }} />
                <section className="login-card" role="dialog" aria-label="Continue password reset">
                    <header className="login-head">
                        <img src={polarisLogo} alt="Polaris Athlete" className="login-logo" />
                        <p className="welcome">
                            Hi <strong>{greetingName}</strong>! Continue to sign in and update your password
                            {invitation?.email ? <> for <strong>{invitation.email}</strong></> : null}.
                        </p>
                    </header>

                    {error && <div className="form-alert is-error" role="alert">{error}</div>}

                    <button
                        type="button"
                        className="login-btn-primary"
                        disabled={isSubmitting}
                        onClick={() => void handleResetContinue()}
                    >
                        {isSubmitting ? 'Signing in…' : 'Continue to reset password'}
                    </button>
                </section>
            </main>
        );
    }

    return (
        <main className="login-page" style={{ backgroundColor: '#000000' }} data-testid="invitation-set-password">
            <div className="login-overlay" style={{ backgroundColor: '#00000066' }} />
            <section className="login-card" role="dialog" aria-label="Set your password">
                <header className="login-head">
                    <img src={polarisLogo} alt="Polaris Athlete" className="login-logo" />
                    <p className="welcome">
                        Welcome, <strong>{greetingName}</strong>! Set your password to activate your account
                        {invitation?.email ? <> for <strong>{invitation.email}</strong></> : null}.
                    </p>
                </header>

                <form onSubmit={handleSetupSubmit} className="login-form">
                    {error && <div className="form-alert is-error" role="alert">{error}</div>}

                    <div className="field">
                        <label htmlFor="invitation-password">Password</label>
                        <input
                            id="invitation-password"
                            name="password"
                            type="password"
                            autoComplete="new-password"
                            className="input"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                            autoFocus
                        />
                    </div>

                    <div className="field">
                        <label htmlFor="invitation-confirm-password">Confirm password</label>
                        <input
                            id="invitation-confirm-password"
                            name="confirmPassword"
                            type="password"
                            autoComplete="new-password"
                            className="input"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength={8}
                        />
                    </div>

                    <p className="welcome" style={{ fontSize: 13, marginBottom: 0 }}>
                        Must be at least 8 characters with one digit, one lowercase, one uppercase,
                        and one special character (@#$%^&+=).
                    </p>

                    <button
                        type="submit"
                        className="login-btn-primary"
                        disabled={isSubmitting || !password || !confirmPassword}
                    >
                        {isSubmitting ? 'Activating account…' : 'Set password & continue'}
                    </button>
                </form>
            </section>
        </main>
    );
}
